import { useEffect, useState } from "react";
import { api, ApiError, type PublicPolicy } from "../../lib/api";
import { PolicyBody } from "./PolicyBody";
import { PolicySupplements } from "./PolicySupplements";

export function PolicyMetadata({ document }: { document: Pick<PublicPolicy, "version" | "publishedAt"> }) {
  return <p className="text-xs text-mut">
    {document.version !== null ? `Version ${document.version}` : null}
    {document.publishedAt ? `${document.version !== null ? " · " : ""}Published ${new Date(document.publishedAt).toLocaleDateString()}` : null}
  </p>;
}

/** Current readers and Admin saved snapshots share safe body and source-owned
 * supplements. Callers own fetching/version selection; this never accepts terms. */
export function PolicyContent({ kind, body }: { kind: string; body: string | null }) {
  return <>{body !== null && <article><PolicyBody body={body} /></article>}<PolicySupplements key={kind} kind={kind} /></>;
}

/** Fetches current text on every opening. Failures retain the caller's catalog card;
 * only an actual 404 is absence. Reading never invokes legal acceptance. */
export function PolicyReader({ kind, onDocument }: {
  kind: string; onDocument?: (document: PublicPolicy | null) => void;
}) {
  const [document, setDocument] = useState<PublicPolicy | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "absent" | "error">("loading");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setDocument(null); setState("loading"); onDocument?.(null);
    api.publishedPolicy(kind).then((policy) => {
      if (active) { setDocument(policy); setState("ready"); onDocument?.(policy); }
    }).catch((error: unknown) => {
      if (active) setState(error instanceof ApiError && error.status === 404 ? "absent" : "error");
    });
    return () => { active = false; };
  }, [kind, attempt, onDocument]);

  return <>
    {state === "loading" && <p role="status" className="text-mut">Loading document…</p>}
    {state === "absent" && <p className="text-mut">No published document is available.</p>}
    {state === "error" && <div role="alert" className="text-brand-red">
      <p>Could not load this policy. Please try again.</p>
      <button type="button" className="btn-outline mt-3" onClick={() => setAttempt((value) => value + 1)}>Retry document</button>
    </div>}
    <PolicyContent kind={kind} body={state === "ready" && document ? document.body : null} />
  </>;
}
